import { useState, useEffect, useRef } from "react";
import axios from "axios";

const ChatBubble = ({ onClose, productId }) => {
  const [input, setInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatContainerRef = useRef(null);

  // Cada vez que se actualicen los mensajes, se hace scroll hasta el fondo
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Agrega el mensaje del usuario al historial
    const userMessage = { sender: "user", text: input };
    setChatMessages((prev) => [...prev, userMessage]);

    // Limpia el input y muestra el loader
    setInput("");
    setLoading(true);

    try {
      // Llamada a tu backend en inglés
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/product-ai/${productId}/`,
        { message: input }
      );

      // Agregamos un mensaje vacío para el bot, que iremos completando
      const botEmptyMessage = { sender: "bot", text: "" };
      setChatMessages((prev) => [...prev, botEmptyMessage]);

      // Efecto de escritura: se muestra la respuesta carácter a carácter
      const fullBotMessage = response.data.answer;
      let index = 0;
      const intervalId = setInterval(() => {
        index++;
        setChatMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1].text =
            fullBotMessage.slice(0, index);
          return updatedMessages;
        });

        if (index === fullBotMessage.length) {
          clearInterval(intervalId);
        }
      }, 40);
    } catch (error) {
      const errorMessage = {
        sender: "bot",
        text: "Sorry, an error has occurred.",
      };
      setChatMessages((prev) => [...prev, errorMessage]);
    }
    setLoading(false);
  };

  return (
    <div className="
      fixed
      bottom-4
      right-4
      w-11/12
      md:w-96
      bg-white
      rounded-lg
      shadow-xl
      overflow-hidden
      transition-all
      duration-300
      z-50
    ">
      {/* Chat Header */}
      <div
        className="bg-custom-green-button text-white p-4 flex items-center justify-between"
        data-oid="ix::-jn"
      >
        <div className="flex items-center" data-oid="hdrzdfr">
          <div
            className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-custom-green-button mr-3"
            data-oid="mu1xw0e"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
              data-oid="7wuyy18"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                data-oid="unk8cu7"
              />
            </svg>
          </div>
          <div data-oid="3s7srgt">
            <h3 className="font-medium" data-oid="724:b5:">
              Histori AI Assistant
            </h3>
            <p className="text-xs text-indigo-100" data-oid="aw4e_gn">
              Available 24/7
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-indigo-100 hover:text-white"
          data-oid="6ig5xxb"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6"
            data-oid="tremx38"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
              data-oid="16lnpid"
            />
          </svg>
        </button>
      </div>

      {/* Chat Messages */}
      <div
        ref={chatContainerRef}
        className="h-72 md:h-80 overflow-y-auto p-4 bg-slate-50"
        data-oid="qklv835"
      >
        {chatMessages.map((msg, index) => (
          <div
            key={index}
            className={`mb-4 flex ${
              msg.sender === "user" ? "justify-end" : "justify-start"
            }`}
            data-oid="m.vkonu"
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                msg.sender === "user"
                  ? "bg-custom-green-button text-white rounded-br-none"
                  : "bg-white text-slate-800 border border-slate-200 rounded-bl-none"
              }`}
              data-oid="jyxdplg"
            >
              <p className="text-sm" data-oid="s.uud7-">
                {msg.text}
              </p>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start mb-4" data-oid="cqkzjz0">
            <div
              className="bg-white text-slate-800 border border-slate-200 rounded-lg rounded-bl-none p-3 max-w-[80%]"
              data-oid=":yj-9sf"
            >
              <div className="flex space-x-1" data-oid=".f:7_7m">
                <div
                  className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
                  data-oid="go0..fw"
                ></div>
                <div
                  className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                  data-oid="53mavaq"
                ></div>
                <div
                  className="w-2 h-2 bg-slate-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.4s" }}
                  data-oid="k72na2q"
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="p-4 border-t border-slate-200" data-oid="av98nep">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(e);
          }}
          className="flex items-center"
          data-oid="c9ha66_"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Write your question here..."
            className="flex-1 border border-slate-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-oid="24vn-v0"
          />
          <button
            type="submit"
            className="bg-custom-green-button text-white px-4 py-2 rounded-r-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-oid="84swpeu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
              data-oid="m80kt6u"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                data-oid="uupp_8w"
              />
            </svg>
          </button>
        </form>
        <p
          className="text-xs text-slate-500 mt-2 text-center"
          data-oid="rjn0xzf"
        >
          This chat is secured with end-to-end encryption
        </p>
      </div>
    </div>
  );
};

export default ChatBubble;
